import { Group } from '@byecode/ui'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { FilterGroupItem, FlowNode } from '@lighthouse/shared'
import { OUTPUT_SINGLE_DATA_NODES } from '@lighthouse/shared'
import { arrayMove, nanoid } from '@lighthouse/tools'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import * as CM from '../styles'
import BranchItem from './BranchItem'

interface ConditionConfigureProps {
    allParentNodes: FlowNode[]
    value: FilterGroupItem[]
    onChange: (value: FilterGroupItem[]) => void
}

const SCxContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 0 8px 4px 8px;
`
const Branch: React.FC<ConditionConfigureProps> = ({ allParentNodes, value, onChange }) => {
    const allSingleParentNodes = useMemo(
        () => allParentNodes.filter(item => OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)),
        [allParentNodes]
    )

    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }))

    const [openedMap, setOpenMap] = useState<Record<string, boolean>>({})
    const fields = useMemo(() => value || [], [value])

    const getInitParam = useCallback(() => {
        const id = nanoid(12)
        return {
            id,
            filter: {}
        }
    }, [])

    // 在添加新字段时，同时更新 openedMap
    const handleAddField = useCallback(() => {
        const newItem = getInitParam()
        onChange([...fields, newItem])
    }, [fields, getInitParam, onChange])

    // if (!fields.length) {
    //     handleAddField()
    // }

    useEffect(() => {
        const newOpenedMap = fields.reduce<Record<string, boolean>>((acc, field) => {
            acc[field.id] = true
            return acc
        }, {})
        setOpenMap(newOpenedMap)
    }, [fields])

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event
            if (active.id !== over?.id) {
                const oldIndex = fields.findIndex(item => item.id === active.id)
                const newIndex = fields.findIndex(item => item.id === over?.id)
                const list = arrayMove(fields, oldIndex, newIndex)
                onChange(list)
            }
        },
        [fields, onChange]
    )

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const { active } = event
        // setActiveId(active?.id.toString())
    }, [])

    return (
        <Group
            label="分支情况"
            extra={[
                <CM.Icon
                    mr={-8}
                    onClick={e => {
                        e.stopPropagation()
                        handleAddField()
                    }}
                    key={1}
                    type="Add2"
                    size={20}
                    color="var(--color-gray-400)"
                />
            ]}
        >
            <SCxContainer>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    onDragStart={handleDragStart}
                    modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                >
                    <SortableContext items={fields.map(d => d.id)} strategy={verticalListSortingStrategy}>
                        {fields.map((item, index) => (
                            <BranchItem
                                key={item.id}
                                index={index}
                                len={fields.length}
                                allSingleParentNodes={allSingleParentNodes}
                                id={item.id}
                                opened={openedMap[item.id]}
                                onChangeOpen={(opened: boolean) => {
                                    setOpenMap(draft => ({
                                        ...draft,
                                        [item.id]: opened
                                    }))
                                }}
                                onChange={filter => {
                                    const next = fields.map((f, i) => (i === index ? { ...f, filter: filter || {} } : f))
                                    onChange(next)
                                }}
                                onDelete={() => {
                                    const next = fields.filter((_, i) => i !== index)
                                    onChange(next)
                                    setOpenMap(draft => ({
                                        ...draft,
                                        [item.id]: false
                                    }))
                                }}
                                value={item.filter}
                            />
                        ))}
                    </SortableContext>

                    <CM.LastContent>
                        <CM.LastContentIndex>情况{fields.length + 1}</CM.LastContentIndex>
                        <CM.LastContentTitle>否则</CM.LastContentTitle>
                    </CM.LastContent>
                </DndContext>
            </SCxContainer>
        </Group>
    )
}

export default Branch
